import { ParamMap, Params } from '@angular/router';
import { IBase } from 'src/app/models';
import { MessageOrigin, MessageReceiver, NotificationEvent } from 'src/app/models/enums';
import { parseEnumList, parseIdList, serializeIdList, serializeStringList } from './filterUtils';

export interface IMessagesFilters {
  selectedClients: IBase[];
  selectedSenders: IBase[];
  selectedReceivers: IBase[];
  selectedOrigin: MessageOrigin | null;
  selectedEvents: NotificationEvent[];
  selectedAdminAlerts: boolean;
}

export interface IMessagesPanelFilters extends IMessagesFilters {
  receiverType?: MessageReceiver;
  selectedExpeditionService?: IBase;
  selectedExpeditionRoute?: IBase;
}

export const MESSAGE_FILTER_KEYS = ['clients', 'senders', 'receivers', 'origin', 'events', 'adminAlerts'] as const;

export function emptyMessagesFilters(): IMessagesFilters {
  return {
    selectedClients: [],
    selectedSenders: [],
    selectedReceivers: [],
    selectedOrigin: null,
    selectedEvents: [],
    selectedAdminAlerts: false,
  };
}

export function serializeMessagesFilters(f: IMessagesFilters): Params {
  return {
    clients: serializeIdList(f.selectedClients),
    senders: serializeIdList(f.selectedSenders),
    receivers: serializeIdList(f.selectedReceivers),
    origin: f.selectedOrigin,
    events: serializeStringList(f.selectedEvents),
    adminAlerts: f.selectedAdminAlerts ? 'true' : null,
  };
}

export function deserializeMessagesFilters(params: ParamMap): IMessagesFilters {
  return {
    selectedClients: parseIdList(params.get('clients')),
    selectedSenders: parseIdList(params.get('senders')),
    selectedReceivers: parseIdList(params.get('receivers')),
    selectedOrigin: parseMessageOrigin(params.get('origin')),
    selectedEvents: parseEnumList<NotificationEvent>(params.get('events')),
    selectedAdminAlerts: params.get('adminAlerts') === 'true',
  };
}

export function parseMessagesFiltersFromStorage(json: string | null | undefined): IMessagesFilters | null {
  if (!json) return null;
  try {
    const f = JSON.parse(json);
    return {
      selectedClients: Array.isArray(f.selectedClients) ? f.selectedClients : [],
      selectedSenders: Array.isArray(f.selectedSenders) ? f.selectedSenders : [],
      // The messages filter modal stores receivers under a nested key for legacy reasons.
      selectedReceivers: Array.isArray(f.receivers?.selectedReceivers)
        ? f.receivers.selectedReceivers
        : (Array.isArray(f.selectedReceivers) ? f.selectedReceivers : []),
      selectedOrigin: parseMessageOrigin(f.selectedOrigin),
      selectedEvents: Array.isArray(f.selectedEvents) ? f.selectedEvents : [],
      selectedAdminAlerts: f.selectedAdminAlerts === true,
    };
  } catch {
    return null;
  }
}

export function parseMessagesPanelFiltersFromStorage(json: string | null | undefined): IMessagesPanelFilters | null {
  const filters = parseMessagesFiltersFromStorage(json);
  if (!filters) return null;
  try {
    const receivers = JSON.parse(json!).receivers;
    return {
      ...filters,
      receiverType: receivers?.selectedReceiver ?? undefined,
      selectedExpeditionService: receivers?.selectedExpeditionService ?? undefined,
      selectedExpeditionRoute: receivers?.selectedExpeditionRoute ?? undefined,
    };
  } catch {
    return filters;
  }
}

export function serializeMessagesPanelFiltersForStorage(f: IMessagesPanelFilters): string {
  return JSON.stringify({
    selectedClients: f.selectedClients,
    selectedSenders: f.selectedSenders,
    selectedOrigin: f.selectedOrigin,
    selectedEvents: f.selectedEvents,
    selectedAdminAlerts: f.selectedAdminAlerts,
    receivers: {
      selectedReceiver: f.receiverType,
      selectedExpeditionService: f.selectedExpeditionService,
      selectedExpeditionRoute: f.selectedExpeditionRoute,
      selectedReceivers: f.selectedReceivers,
    },
  });
}

export function toAutomaticFlag(origin: MessageOrigin | null): boolean | null {
  if (origin === null) return null;
  return origin === MessageOrigin.AUTOMATIC;
}

export function toMessageOrigin(checkedOrigins: string[]): MessageOrigin | null {
  return checkedOrigins.length === 1 ? parseMessageOrigin(checkedOrigins[0]) : null;
}

export function toCheckedOrigins(origin: MessageOrigin | null): MessageOrigin[] {
  return origin ? [origin] : Object.values(MessageOrigin);
}

function parseMessageOrigin(raw: string | null | undefined): MessageOrigin | null {
  return Object.values(MessageOrigin).includes(raw as MessageOrigin) ? (raw as MessageOrigin) : null;
}
